import ButtonPlayerView from "shared/components/buttons/ButtonPlayerView.js";
import ItemRowPlayerAssist from "shared/components/items/ItemRowPlayerAssist.js";
import LoadingSquare from "shared/components/loading/LoadingSquare";
import React, { useEffect, useState } from "react";
import { useOutletContext } from "react-router-dom";
import { getPlayers, getClubs } from "shared/services/api-assistant.js";
import FilterContainerPlayer from "shared/components/filters/FilterContainerPlayer.js";
import PopupInformationPricing from "shared/components/popups/PopupInformationPricing.js";
import BoxCard from "shared/components/box/BoxCard.js";
import { getPlayerPricing } from "shared/utils/player.js";
import { dateToDayString } from "shared/utils/date.js";
import Count from "shared/components/counts/Count.js";
import { useNavigate } from "react-router-dom";
import PopupPremiumInfo from "shared/components/popups/PopupPremiumInfo.js";
import PopupTermsAndConditions from "shared/components/popups/PopupTermsAndConditions.js";
import { getArenaManager } from "shared/services/api-mfl.js";

const PageUserOverview = (props) => {
  const user = useOutletContext();
  const navigate = useNavigate();
  const [players, setPlayers] = useState(null);
  const [clubs, setClubs] = useState(null);
  const [arenaPoints, setArenaPoints] = useState(null);

  const fetchPlayers = () => {
    setPlayers(null);

    getPlayers({
      handleSuccess: (d) => {
        setPlayers(d.data.getPlayers);
      },
      handleError: (e) => console.log(e),
      params: {
        owners: [user.id],
        limit: 50000,
        activePlayersOnly: true,
      },
    });
  };

  const fetchClubs = () => {
    getClubs({
      handleSuccess: (d) => {
        setClubs(d.data.getClubs.sort((a, b) => a.division - b.division));
      },
      handleError: (e) => console.log(e),
      params: { owners: [user.id], considerStaffMembers: false },
    });
  };

  function hideEmail(email) {
    if (!email) return "";

    const [name, domain] = email.split("@");

    if (!domain) return email;

    const visibleChars = name.length <= 2 ? 1 : 2;

    const hidden = "*".repeat(Math.max(name.length - visibleChars, 2));

    return `${name.substring(0, visibleChars)}${hidden}@${domain}`;
  }

  useEffect(() => {
    if (user) {
      fetchPlayers();
      fetchClubs();
    }
  }, []);

  useEffect(() => {
    if (!user?.address) return;
    const controller = new AbortController();
    getArenaManager({ address: user.address, signal: controller.signal })
      .then((manager) => {
        if (!controller.signal.aborted) setArenaPoints(manager?.ladder?.ap ?? "--");
      })
      .catch(() => {
        if (!controller.signal.aborted) setArenaPoints("--");
      });
    return () => controller.abort();
  }, [user?.address]);

  return (
    <div id="PageUserOverview">
      <div className="d-flex container max-width-md px-1 px-md-4 py-4">
        <div className="d-flex flex-fill flex-column flex-md-row">
          <div className="d-flex flex-column flex-md-basis-200">
            <BoxCard
              title={
                <div style={{ minWidth: "100px" }}>
                  {players !== null ? (
                    <Count label="Players" count={players.length} />
                  ) : (
                    <LoadingSquare height={"50px"} />
                  )}
                </div>
              }
              content={
                <div className="d-flex flex-fill justify-content-end">
                  <button className="btn btn-link primary-color" onClick={() => navigate(`/user/${user.address}/players`)}>
                    Go to players
                    <i className="bi bi-arrow-right ms-1" aria-hidden="true" />
                  </button>
                </div>
              }
            ></BoxCard>
            <BoxCard
              title={
                <div style={{ minWidth: "100px" }}>
                  {clubs !== null ? (
                    <Count label="Clubs" count={clubs.filter((club) => club.type !== "ARENA").length} />
                  ) : (
                    <LoadingSquare height={"50px"} />
                  )}
                </div>
              }
              content={
                <div className="d-flex flex-fill justify-content-end">
                  <button className="btn btn-link primary-color" onClick={() => navigate(`/user/${user.address}/clubs`)}>
                    Go to clubs
                    <i className="bi bi-arrow-right ms-1" aria-hidden="true" />
                  </button>
                </div>
              }
            ></BoxCard>
            <BoxCard
              title={
                <div style={{ minWidth: "100px" }}>
                  {arenaPoints !== null ? (
                    <Count label="Arena Points" count={arenaPoints} />
                  ) : (
                    <LoadingSquare height={"50px"} />
                  )}
                </div>
              }
            />
          </div>
          <div className="d-flex flex-grow-1 flex-column">
            <BoxCard
              title={"Profile"}
              content={
                <div className="d-flex flex-column flex-fill background-color px-2 py-1">
                  <div className="d-flex justify-content-between align-items-center">
                    <span className="text-muted">Name</span>
                    <span>{user?.name}</span>
                  </div>

                  <div className="d-flex justify-content-between align-items-center border-top">
                    <span className="text-muted">Address</span>
                    <span className="text-end">{user?.address}</span>
                  </div>

                  <div className="d-flex justify-content-between align-items-center border-top">
                    <span className="text-muted">Country</span>
                    <span>{user?.country}</span>
                  </div>

                  <div className="d-flex justify-content-between align-items-center border-top">
                    <span className="text-muted">City</span>
                    <span>{user?.city}</span>
                  </div>
                </div>
              }
            ></BoxCard>
            {user.address === props.assistantUser?.address && (
              <BoxCard
                title={"Premium"}
                content={
                  <div className="d-flex flex-column flex-fill">
                    <div className="d-flex flex-column flex-fill background-color px-2 py-1 mb-1">
                      <div className="d-flex justify-content-between align-items-center">
                        <span className="text-muted">Premium</span>
                        <span>{props.assistantUser?.premiumEndDate >= dateToDayString(new Date()) ? "Yes" : "No"}</span>
                      </div>

                      <div className="d-flex justify-content-between align-items-center border-top">
                        <span className="text-muted">End date</span>
                        <span className="text-end">
                          {props.assistantUser?.premiumEndDate >= dateToDayString(new Date())
                            ? props.assistantUser?.premiumEndDate
                            : "--"}
                        </span>
                      </div>
                    </div>

                    <stripe-pricing-table
                      pricing-table-id="prctbl_1Tv08ULmTDYuvMT7e6BQoJku"
                      publishable-key="pk_live_51Qwm4hLmTDYuvMT7G8j1Xk7pCzA8fS1QvGEPRwxAihZJtyybG4HkH2rqcDRjHuxy15cWFHOrykik99FaHiLKuCSk00OMGcWype"
                    ></stripe-pricing-table>
                    <div className="d-flex flex-fill justify-content-end mt-2">
                      <PopupTermsAndConditions
                        trigger={
                          <button className="btn btn-link">
                            <i className="bi bi-file-earmark-text me-1"></i>Terms And Conditions
                          </button>
                        }
                      />
                      <PopupPremiumInfo
                        trigger={
                          <button className="btn btn-premium">
                            <i className="bi bi-stars"></i> More info
                          </button>
                        }
                      />
                    </div>
                  </div>
                }
              ></BoxCard>
            )}

            {user.address === props.assistantUser?.address && (
              <BoxCard
                className="w-100"
                title={"Email"}
                content={
                  <div className="d-flex flex-column flex-fill justify-content-end">
                    <div className="d-flex flex-column flex-fill background-color px-2 py-1">
                      <div className="d-flex justify-content-between align-items-center">
                        <span className="text-muted">Email</span>
                        <span>{hideEmail(props.assistantUser?.email)}</span>
                      </div>

                      <div className="d-flex justify-content-between align-items-center border-top">
                        <span className="text-muted">Confirmed</span>
                        <span>{props.assistantUser?.isEmailConfirmed ? "Yes" : "No"}</span>
                      </div>
                    </div>
                    <div className="d-flex flex-fill justify-content-end mt-2">
                      <button
                        className="btn btn-danger text-white"
                        onClick={() => navigate("/user/" + user.address + "/players")}
                      >
                        Delete email
                      </button>
                    </div>
                  </div>
                }
              ></BoxCard>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default PageUserOverview;
